import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Grid,
  Container,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { getTransactions, grantCredits, getUserDetails } from '../../api/patient';
import { getTransactionHistory } from '../../api/history';

type Transaction = {
  id_servizio: number;
  quantita: number;
};

type TransactionHistory = {
  id_movimento: number;
  id_utente: number;
  id_servizio: number;
  quantita: number;                // + acquisto / - consumo
  timestamp_movimento: string;
  // opzionali: se la tua API li restituisce
  causale?: string | null;
  amount_cents?: number | null;
  tipo?: string | null;           // 'purchase' / 'consume' (se presente)
};

type UserDetails = {
  nome: string;
  cognome: string;
  email?: string;
  codice_fiscale?: string;
  // campi geografici opzionali dalla tua fetch completa
  denominazione_regione?: string;
  denominazione_provincia?: string;
  denominazione_comune?: string;
  sigla_provincia?: string;
  // eventuali altri
  indirizzo?: string;
  residenza?: string;
};

const cardSx = {
  bgcolor: 'background.paper',
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: 'none',
};

const fmtDateTime = (d: string) =>
  new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmtDate = (d: string) =>
  new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });

const fmtEUR = (v: number) =>
  new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(v);

const PatientDetail: React.FC = () => {
  const { id_utente } = useParams<{ id_utente: string }>();

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [transactionHistory, setTransactionHistory] = useState<TransactionHistory[]>([]);
  const [userDetails, setUserDetails] = useState<UserDetails | null>(null);

  const [loading, setLoading] = useState(true);
  const [credits, setCredits] = useState('');
  const [selectedService, setSelectedService] = useState('1');

  // caricamento dati
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const [transactionsData, historyData, userData] = await Promise.all([
          getTransactions(Number(id_utente)),
          getTransactionHistory(Number(id_utente)),
          getUserDetails(Number(id_utente)),
        ]);

        setTransactions(transactionsData ?? []);
        // ordina cronologicamente desc
        const hist = (historyData ?? []).slice().sort(
          (a, b) =>
            new Date(b.timestamp_movimento).getTime() -
            new Date(a.timestamp_movimento).getTime()
        );
        setTransactionHistory(hist);
        setUserDetails(userData ?? null);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id_utente]);

  const handleGrantCredits = async () => {
    try {
      await grantCredits(Number(id_utente), Number(credits));
      setCredits('');

      // reload liste dopo assegnazione
      const [transactionsData, historyData] = await Promise.all([
        getTransactions(Number(id_utente)),
        getTransactionHistory(Number(id_utente)),
      ]);

      setTransactions(transactionsData ?? []);
      const hist = (historyData ?? []).slice().sort(
        (a, b) =>
          new Date(b.timestamp_movimento).getTime() -
          new Date(a.timestamp_movimento).getTime()
      );
      setTransactionHistory(hist);
    } catch (error) {
      console.error('Error granting credits:', error);
    }
  };

  // util address
  const fullAddress = useMemo(() => {
    if (!userDetails) return '—';
    const p: string[] = [];
    const addr =
      userDetails.indirizzo ||
      userDetails.residenza ||
      '';
    const comune =
      userDetails.denominazione_comune || '';
    const prov =
      userDetails.denominazione_provincia ||
      userDetails.sigla_provincia ||
      '';
    const regione = userDetails.denominazione_regione || '';

    if (addr) p.push(addr);
    if (comune) p.push(comune);
    if (prov) p.push(prov);
    if (regione) p.push(regione);

    return p.filter(Boolean).join(' - ') || '—';
  }, [userDetails]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: 'calc(100vh - 64px)' }}>
      {/* Titolo pagina */}
      <Box sx={{ px: { xs: 3, md: 6 }, pt: 2, pb: 1 }}>
        <Typography variant="h5" sx={{ color: '#fff', fontWeight: 800 }}>
          Dettaglio Crediti – Utente #{id_utente}
        </Typography>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1 }}>
          Gestione crediti, dati anagrafici e storico movimenti dell’utente selezionato.
        </Typography>
      </Box>

      <Container maxWidth="xl" sx={{ pb: 3 }}>
        <Grid container spacing={2}>
          {/* Colonna sinistra: anagrafica + storico */}
          <Grid item xs={12} md={7} lg={8}>
            {/* Anagrafica */}
            <Card sx={{ ...cardSx, p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Dati anagrafici
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Informazioni anagrafiche e di contatto dell’utente
              </Typography>

              <CardContent sx={{ pt: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}
                    >
                      <Typography color="text.secondary">Nome</Typography>
                      <Typography fontWeight={600}>{userDetails?.nome || '—'}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}
                    >
                      <Typography color="text.secondary">Cognome</Typography>
                      <Typography fontWeight={600}>{userDetails?.cognome || '—'}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}
                    >
                      <Typography color="text.secondary">Email</Typography>
                      <Typography fontWeight={600}>{userDetails?.email || '—'}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}
                    >
                      <Typography color="text.secondary">Codice Fiscale</Typography>
                      <Typography fontWeight={600}>{userDetails?.codice_fiscale || '—'}</Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 2 }}
                    >
                      <Typography color="text.secondary">Indirizzo / Comune / Provincia / Regione</Typography>
                      <Typography fontWeight={600} textAlign="right" sx={{ ml: 2 }}>
                        {fullAddress}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>

            {/* Storico movimenti */}
            <Card sx={{ ...cardSx, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Storico Movimenti
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Elenco completo dei movimenti in ordine cronologico (più recenti in alto)
              </Typography>
              <Divider sx={{ mb: 1 }} />

              <TableContainer sx={{ maxHeight: 530 }}>
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data</TableCell>
                      <TableCell>Tipo</TableCell>
                      <TableCell>Servizio</TableCell>
                      <TableCell>Causale</TableCell>
                      <TableCell align="right">Quantità</TableCell>
                      <TableCell align="right">Importo</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactionHistory.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                          Nessun movimento per questo utente
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactionHistory.map((row) => {
                        const eur = (row.amount_cents ?? null) !== null ? (Number(row.amount_cents) / 100) : null;
                        // tipo fallback: se non presente -> quantita > 0 acquisto, < 0 consumo
                        const tipo = row.tipo || (row.quantita >= 0 ? 'purchase' : 'consume');
                        return (
                          <TableRow key={row.id_movimento} hover>
                            <TableCell>{fmtDateTime(row.timestamp_movimento)}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={tipo === 'purchase' ? 'Acquisto' : 'Consumo'}
                                color={tipo === 'purchase' ? 'success' : 'warning'}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>Servizio {row.id_servizio}</TableCell>
                            <TableCell sx={{ maxWidth: 380 }}>
                              <Typography variant="body2" noWrap title={row.causale || ''}>
                                {row.causale || '—'}
                              </Typography>
                            </TableCell>
                            <TableCell align="right" sx={{ color: row.quantita < 0 ? 'error.main' : 'success.main', fontWeight: 600 }}>
                              {row.quantita > 0 ? `+${row.quantita}` : row.quantita}
                            </TableCell>
                            <TableCell align="right">
                              {eur !== null ? fmtEUR(eur) : '—'}
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>

          {/* Colonna destra: operazioni crediti + riepilogo */}
          <Grid item xs={12} md={5} lg={4}>
            {/* Aggiungi crediti */}
            <Card sx={{ ...cardSx, p: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Aggiungi Crediti
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Conferisci crediti all’utente selezionando il servizio e la quantità.
              </Typography>

              <CardContent sx={{ pt: 0 }}>
                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>Servizio</InputLabel>
                    <Select
                      value={selectedService}
                      label="Servizio"
                      onChange={(e) => setSelectedService(e.target.value as string)}
                    >
                      <MenuItem value="1">Composizione corporea</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    label="Quantità"
                    type="number"
                    inputProps={{ min: 1 }}
                    value={credits}
                    onChange={(e) => setCredits(e.target.value)}
                    fullWidth
                  />

                  <Button
                    variant="contained"
                    onClick={handleGrantCredits}
                    disabled={!credits || Number(credits) <= 0}
                  >
                    Conferma
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            {/* Riepilogo crediti attuali */}
            <Card sx={{ ...cardSx, p: 2 }}>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Crediti attuali
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Quantità totali disponibili per ogni servizio
              </Typography>
              <Divider sx={{ mb: 1 }} />

              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Servizio</TableCell>
                      <TableCell align="right">Totale</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {transactions.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>
                          Nessun credito disponibile
                        </TableCell>
                      </TableRow>
                    ) : (
                      transactions.map((row, idx) => (
                        <TableRow key={`${row.id_servizio}-${idx}`}>
                          <TableCell>Servizio {row.id_servizio}</TableCell>
                          <TableCell align="right">
                            <Chip
                              size="small"
                              label={row.quantita}
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default PatientDetail;
